import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getDuelById, deleteDuel, startDuel, getDuelInvites, spectateDuel } from '../api/duels';
import { UserContext } from '../context/UserContext';
import { isWebSocketConnected, subscribeToDuel } from '../api/websocket';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbModal from '../components/controls/HrnbModal';
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import InviteModal from '../components/duels/InviteModal';
import InviteList from '../components/duels/InviteList';
import DuelVoting from '../components/duels/DuelVoting';
import DuelVotingResults from '../components/duels/DuelVotingResults';
import DuelJudgeVerdicts from '../components/duels/DuelJudgeVerdicts';
import DuelSpectatorComments from '../components/duels/DuelSpectatorComments';
import { RegisterIcon } from '../icons';
import '../css/duels/DuelDetail.css';

/**
 * Страница деталей дуэли
 */
function DuelDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);

    const [duel, setDuel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [invites, setInvites] = useState([]);
    const [invitesLoading, setInvitesLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [isJudge, setIsJudge] = useState(false);

    // Загрузка деталей дуэли
    useEffect(() => {
        const loadDuel = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await getDuelById(id);
                setDuel(data);
            } catch (err) {
                console.error('Ошибка при загрузке дуэли:', err);
                // Обработка ошибок для неавторизованных пользователей
                if (err.response?.status === 401 || err.response?.status === 403) {
                    setError('Для просмотра деталей дуэли необходимо войти в систему');
                } else if (err.response?.status === 404) {
                    setError('Дуэль не найдена. Возможно, она была удалена.');
                } else {
                    setError('Не удалось загрузить дуэль.');
                }
            } finally {
                setLoading(false);
            }
        };

        loadDuel();
    }, [id]);

    // Загрузка приглашений
    useEffect(() => {
        if (id && activeTab === 'invites') {
            loadInvites();
        }
    }, [id, activeTab]);

    // WebSocket подписка на обновления дуэли
    useEffect(() => {
        if (!id || !isWebSocketConnected()) {
            console.log('[DuelDetailPage] WebSocket не подключён');
            return;
        }

        console.log('[DuelDetailPage] Подписка на WebSocket события дуэли:', id);

        const unsubscribe = subscribeToDuel(id, (event) => {
            console.log('[DuelDetailPage] Duel event:', event);

            // Обработка событий дуэли
            switch (event?.eventType) {
                case 'DUEL_UPDATED':
                case 'DUEL_STARTED':
                case 'DUEL_FINISHED':
                case 'DUEL_CANCELLED':
                    // Обновляем данные дуэли
                    getDuelById(id).then(setDuel).catch(console.error);
                    break;
                case 'PARTICIPANT_JOINED':
                case 'PARTICIPANT_LEFT':
                    // Обновился список участников
                    getDuelById(id).then(setDuel).catch(console.error);
                    break;
                case 'INVITE_SENT':
                case 'INVITE_ACCEPTED':
                case 'INVITE_REJECTED':
                    // Обновляем приглашения
                    if (activeTab === 'invites') {
                        loadInvites();
                    }
                    // Обновляем данные дуэли
                    getDuelById(id).then(setDuel).catch(console.error);
                    break;
                case 'ROUND_STARTED':
                case 'ROUND_FINISHED':
                case 'VOTE_SUBMITTED':
                    // Обновление раунда или голосования
                    getDuelById(id).then(setDuel).catch(console.error);
                    break;
                default:
                    console.log('[DuelDetailPage] Неизвестное событие:', event);
            }
        });

        return () => {
            console.log('[DuelDetailPage] Отписка от WebSocket событий');
            if (unsubscribe) {
                unsubscribe.unsubscribe();
            }
        };
    }, [id, activeTab]);

    const loadInvites = async () => {
        setInvitesLoading(true);
        try {
            const data = await getDuelInvites(id);
            setInvites(data || []);
        } catch (err) {
            console.error('Ошибка при загрузке приглашений:', err);
        } finally {
            setInvitesLoading(false);
        }
    };

    const handleInviteSuccess = () => {
        loadInvites();
        // Обновляем данные дуэли
        getDuelById(id).then(setDuel).catch(console.error);
    };

    // Проверка прав создателя
    const isCreator = currentUser && duel?.creator?.id === currentUser.id;

    // Статусы
    const statusLabels = {
        PENDING_PARTICIPANTS: 'Ожидание участников',
        READY_TO_START: 'Готово к началу',
        IN_PROGRESS: 'В процессе',
        FINISHED: 'Завершено',
        CANCELLED: 'Отменено'
    };

    const formatLabels = {
        SOLO: '1 на 1',
        TEAM_2V2: '2 на 2',
        TEAM_3V3: '3 на 3'
    };

    const roundTypeLabels = {
        SINGLE_ROUND: '1 раунд',
        BEST_OF_THREE: 'До 3 побед'
    };

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Обработчик начала дуэли
    const handleStart = async () => {
        setActionLoading(true);
        try {
            await startDuel(id);
            // Обновляем данные
            const updatedDuel = await getDuelById(id);
            setDuel(updatedDuel);
        } catch (err) {
            console.error('Ошибка при старте дуэли:', err);
            setError(err.response?.data?.message || 'Не удалось начать дуэль');
        } finally {
            setActionLoading(false);
        }
    };

    // Обработчик удаления
    const handleDelete = async () => {
        setActionLoading(true);
        try {
            await deleteDuel(id);
            navigate('/duels');
        } catch (err) {
            console.error('Ошибка при удалении дуэли:', err);
            setError(err.response?.data?.message || 'Не удалось удалить дуэль');
        } finally {
            setActionLoading(false);
            setShowDeleteModal(false);
        }
    };

    // Обработчик приглашения - проверка авторизации
    const handleInviteClick = () => {
        if (!currentUser) {
            setShowAuthModal(true);
        } else {
            setShowInviteModal(true);
        }
    };

    // URL обложки
    const coverUrl = duel?.coverImageId
        ? `${process.env.REACT_APP_API_URL || 'https://api.hrnb.ru/api'}/files/${duel.coverImageId}`
        : null;

    if (loading) {
        return <HrnbLoader />;
    }

    if (error && !duel) {
        const isAuthError = error?.includes('необходимо войти');
        return (
            <div className="duel-detail">
                <HrnbAlert variant="danger">{error}</HrnbAlert>
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                    <Link to="/duels">
                        <HrnbButton variant="secondary">Вернуться к списку</HrnbButton>
                    </Link>
                    {isAuthError && (
                        <Link to="/login">
                            <HrnbButton variant="primary">Войти</HrnbButton>
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="duel-detail">
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {coverUrl && (
                <div className="duel-detail__cover">
                    <img src={coverUrl} alt={duel.title} className="duel-detail__image" />
                </div>
            )}

            <HrnbTabs value={activeTab} onChange={setActiveTab}>
                <HrnbTab label="Информация" value="info" />
                <HrnbTab label="Голосование" value="voting" />
                <HrnbTab label="Вердикты" value="verdicts" />
                <HrnbTab label="Приглашения" value="invites" />
                <HrnbTab label="Комментарии" value="comments" />
            </HrnbTabs>

            <div className="duel-detail__content">
                {activeTab === 'info' && (
                    <>
                <div className="duel-detail__header">
                    <div className="duel-detail__badges">
                        <span className="duel-detail__status">
                            {statusLabels[duel.status] || duel.status}
                        </span>
                        <span className="duel-detail__format">
                            {formatLabels[duel.format]} • {roundTypeLabels[duel.roundType]}
                        </span>
                        <span className={`duel-detail__ws-status ${isWebSocketConnected() ? 'duel-detail__ws-status--online' : ''}`}>
                            {isWebSocketConnected() ? '🟢 Онлайн' : '🟡 Оффлайн'}
                        </span>
                    </div>

                    <h1 className="duel-detail__title">{duel.title}</h1>
                </div>

                {duel.description && (
                    <p className="duel-detail__description">{duel.description}</p>
                )}

                <div className="duel-detail__meta">
                    <div className="duel-detail__meta-item">
                        <span className="duel-detail__meta-label">Создатель:</span>
                        <Link 
                            to={`/profile/${duel.creator?.profileId || duel.creator?.id}`}
                            className="duel-detail__meta-value"
                        >
                            {duel.creator?.nickname || duel.creator?.name || '—'}
                        </Link>
                    </div>

                    <div className="duel-detail__meta-item">
                        <span className="duel-detail__meta-label">Участников:</span>
                        <span className="duel-detail__meta-value">
                            {duel.participantsCount} / {duel.requiredParticipants}
                        </span>
                    </div>

                    <div className="duel-detail__meta-item">
                        <span className="duel-detail__meta-label">Создано:</span>
                        <span className="duel-detail__meta-value">
                            {formatDate(duel.createdAt)}
                        </span>
                    </div>

                    {duel.startedAt && (
                        <div className="duel-detail__meta-item">
                            <span className="duel-detail__meta-label">Начато:</span>
                            <span className="duel-detail__meta-value">
                                {formatDate(duel.startedAt)}
                            </span>
                        </div>
                    )}
                </div>

                {/* Участники */}
                {duel.participants && duel.participants.length > 0 && (
                    <div className="duel-detail__section">
                        <h3 className="duel-detail__section-title">Участники</h3>
                        <div className="duel-detail__participants">
                            {duel.participants.map((participant, index) => (
                                <div key={participant.id || index} className="duel-detail__participant">
                                    <span className="duel-detail__participant-name">
                                        {participant.nickname || participant.name}
                                    </span>
                                    {participant.teamNumber && (
                                        <span className="duel-detail__participant-team">
                                            Команда {participant.teamNumber}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Раунды */}
                {duel.rounds && duel.rounds.length > 0 && (
                    <div className="duel-detail__section">
                        <h3 className="duel-detail__section-title">Раунды</h3>
                        <div className="duel-detail__rounds">
                            {duel.rounds.map((round, index) => (
                                <div key={round.id || index} className="duel-detail__round">
                                    <span className="duel-detail__round-title">
                                        Раунд {round.roundNumber || index + 1}
                                    </span>
                                    <span className={`duel-detail__round-status duel-detail__round-status--${round.status?.toLowerCase()}`}>
                                        {round.status || 'Ожидание'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Действия */}
                <div className="duel-detail__actions">
                    {isCreator && duel.status === 'READY_TO_START' && (
                        <HrnbButton
                            variant="success"
                            onClick={handleStart}
                            disabled={actionLoading}
                        >
                            {actionLoading ? 'Начало...' : 'Начать дуэль'}
                        </HrnbButton>
                    )}

                    <HrnbButton
                        variant="primary"
                        onClick={handleInviteClick}
                        disabled={actionLoading}
                    >
                        Пригласить участника
                    </HrnbButton>

                    {isCreator && duel.status === 'PENDING_PARTICIPANTS' && (
                        <HrnbButton
                            variant="danger"
                            onClick={() => setShowDeleteModal(true)}
                            disabled={actionLoading}
                        >
                            Удалить дуэль
                        </HrnbButton>
                    )}

                    <Link to="/duels">
                        <HrnbButton variant="secondary">
                            Назад к списку
                        </HrnbButton>
                    </Link>
                </div>
                    </>
                )}

                {activeTab === 'voting' && (
                    <div className="duel-detail__voting">
                        <DuelVoting duelId={id} duel={duel} />
                        <DuelVotingResults duelId={id} duel={duel} />
                    </div>
                )}

                {activeTab === 'verdicts' && (
                    <div className="duel-detail__verdicts">
                        <DuelJudgeVerdicts
                            duelId={id}
                            roundId={duel?.rounds?.[0]?.id}
                            isJudge={isJudge}
                        />
                    </div>
                )}

                {activeTab === 'comments' && (
                    <div className="duel-detail__comments">
                        <DuelSpectatorComments duelId={id} />
                    </div>
                )}

                {activeTab === 'invites' && (
                    <div className="duel-detail__invites">
                        <div className="duel-detail__invites-header">
                            <h2 className="duel-detail__invites-title">Приглашения</h2>
                            <HrnbButton
                                variant="primary"
                                size="sm"
                                onClick={handleInviteClick}
                            >
                                Пригласить
                            </HrnbButton>
                        </div>
                        <InviteList
                            invites={invites}
                            loading={invitesLoading}
                            currentUserId={currentUser?.id}
                            isCreator={isCreator}
                            onInviteAction={handleInviteSuccess}
                        />
                    </div>
                )}
            </div>

            {/* Модальное окно удаления */}
            <HrnbModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Удаление дуэли"
            >
                <p>Вы уверены, что хотите удалить эту дуэль? Это действие нельзя отменить.</p>
                <div className="duel-detail__modal-actions">
                    <HrnbButton
                        variant="danger"
                        onClick={handleDelete}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Удаление...' : 'Удалить'}
                    </HrnbButton>
                    <HrnbButton
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={actionLoading}
                    >
                        Отмена
                    </HrnbButton>
                </div>
            </HrnbModal>

            {/* Модальное окно приглашения */}
            <InviteModal
                open={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                duelId={id}
                onSuccess={handleInviteSuccess}
            />

            {/* Модальное окно для неавторизованных пользователей */}
            <HrnbModal
                open={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                title="Требуется авторизация"
                size="medium"
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ fontSize: '16px', marginBottom: '24px' }}>
                        Для приглашения участников необходимо войти в систему или зарегистрироваться
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                        <Link to="/login">
                            <HrnbButton variant="primary" onClick={() => setShowAuthModal(false)}>
                                Войти
                            </HrnbButton>
                        </Link>
                        <Link to="/register">
                            <HrnbButton variant="outline" onClick={() => setShowAuthModal(false)} icon={<RegisterIcon />}>
                                Регистрация
                            </HrnbButton>
                        </Link>
                    </div>
                </div>
            </HrnbModal>
        </div>
    );
}

export default DuelDetailPage;
